import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  TrashIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';

interface Family {
  id: string;
  name: string;
  description: string | null;
  avatar_url: string | null;
  member_count: number;
  created_at: string | null;
  updated_at: string | null;
}

interface FamilyDetail {
  id: string;
  name: string;
  description: string | null;
  avatar_url: string | null;
  settings: Record<string, any> | null;
  created_at: string | null;
  updated_at: string | null;
  members: {
    id: string;
    user_id: string | null;
    username: string | null;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
    role: string;
    is_active: boolean;
    is_managed: boolean;
    nickname: string | null;
    joined_at: string | null;
  }[];
}

interface FamiliesResponse {
  items: Family[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export default function AdminFamilies() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [viewingFamily, setViewingFamily] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Family | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'families', page, search],
    queryFn: async () => {
      const params: Record<string, any> = { page, per_page: 20 };
      if (search) params.search = search;
      const response = await api.get('/admin/families', { params });
      return response.data as FamiliesResponse;
    },
  });

  const { data: familyDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['admin', 'families', viewingFamily],
    queryFn: async () => {
      const response = await api.get(`/admin/families/${viewingFamily}`);
      return response.data as FamilyDetail;
    },
    enabled: !!viewingFamily,
  });

  const deleteFamilyMutation = useMutation({
    mutationFn: async (familyId: string) => {
      await api.delete(`/admin/families/${familyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'families'] });
      setShowDeleteConfirm(null);
      toast.success('Family deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete family');
    },
  });

  const updateMemberRoleMutation = useMutation({
    mutationFn: async ({ familyId, memberId, role }: { familyId: string; memberId: string; role: string }) => {
      await api.patch(`/admin/families/${familyId}/members/${memberId}/role`, null, {
        params: { role },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'families', viewingFamily] });
      toast.success('Member role updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      owner: 'bg-amber-500/20 text-amber-400',
      admin: 'bg-purple-500/20 text-purple-400',
      member: 'bg-blue-500/20 text-blue-400',
    };
    return colors[role] || 'bg-surface-600 text-surface-300';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Families</h1>
          <p className="text-surface-400 mt-1">
            {data?.total || 0} total families
          </p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by family name..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-surface-800 border border-surface-600 text-white placeholder-surface-400 focus:outline-none focus:border-amber-500"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 rounded-lg bg-surface-700 hover:bg-surface-600 text-white transition-colors"
        >
          Search
        </button>
      </form>

      {/* Families Table */}
      <div className="bg-surface-800/50 rounded-xl border border-surface-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Family</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Description</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Members</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Created</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-700">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-surface-400">
                    No families found
                  </td>
                </tr>
              ) : (
                data?.items.map((family) => (
                  <motion.tr
                    key={family.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-surface-700/30 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {family.avatar_url ? (
                          <img
                            src={family.avatar_url}
                            alt=""
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-purple-500/20 flex items-center justify-center">
                            <UsersIcon className="w-5 h-5 text-amber-400" />
                          </div>
                        )}
                        <span className="font-medium text-white">{family.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-surface-400 max-w-[200px] truncate">
                      {family.description || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-surface-700 text-surface-300">
                        {family.member_count} members
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-surface-400">
                      {formatDate(family.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setViewingFamily(family.id)}
                          className="p-1.5 rounded-lg text-surface-400 hover:text-white hover:bg-surface-600 transition-colors"
                          title="View details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(family)}
                          className="p-1.5 rounded-lg text-surface-400 hover:text-red-400 hover:bg-red-500/20 transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-surface-700">
            <p className="text-sm text-surface-400">
              Page {data.page} of {data.pages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                disabled={page === data.pages}
                className="p-2 rounded-lg hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Family Detail Modal */}
      <AnimatePresence>
        {viewingFamily && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            onClick={() => setViewingFamily(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface-800 rounded-xl p-6 max-w-2xl w-full border border-surface-700 max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
                </div>
              ) : familyDetail ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      {familyDetail.avatar_url ? (
                        <img
                          src={familyDetail.avatar_url}
                          alt=""
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500/20 to-purple-500/20 flex items-center justify-center">
                          <UsersIcon className="w-6 h-6 text-amber-400" />
                        </div>
                      )}
                      <div>
                        <h2 className="text-xl font-bold text-white">{familyDetail.name}</h2>
                        <p className="text-sm text-surface-400">{familyDetail.description || 'No description'}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setViewingFamily(null)}
                      className="p-1 rounded-lg hover:bg-surface-700 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5 text-surface-400" />
                    </button>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-surface-400">
                      Created: {formatDate(familyDetail.created_at)} • 
                      Updated: {formatDate(familyDetail.updated_at)}
                    </p>
                  </div>

                  <h3 className="text-lg font-semibold text-white mb-3">
                    Members ({familyDetail.members.length})
                  </h3>
                  <div className="space-y-2">
                    {familyDetail.members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-surface-700/50"
                      >
                        <div className="flex items-center gap-3">
                          {member.avatar_url ? (
                            <img
                              src={member.avatar_url}
                              alt=""
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-surface-600 flex items-center justify-center text-surface-400 text-sm font-medium">
                              {(member.first_name || member.username || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-white">
                              {member.first_name || member.nickname || member.username || 'Unknown'}
                              {member.last_name && ` ${member.last_name}`}
                              {member.is_managed && (
                                <span className="ml-2 text-xs text-surface-500">(managed)</span>
                              )}
                            </p>
                            <p className="text-xs text-surface-400">
                              {member.email || member.username || 'No email'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={member.role}
                            onChange={(e) =>
                              updateMemberRoleMutation.mutate({
                                familyId: familyDetail.id,
                                memberId: member.id,
                                role: e.target.value,
                              })
                            }
                            className={`px-2 py-1 rounded text-xs font-medium ${getRoleColor(member.role)} bg-transparent border-none focus:outline-none cursor-pointer`}
                          >
                            <option value="owner">Owner</option>
                            <option value="admin">Admin</option>
                            <option value="member">Member</option>
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-center text-surface-400 py-8">Family not found</p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface-800 rounded-xl p-6 max-w-md w-full border border-surface-700"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-white mb-2">Delete Family</h3>
              <p className="text-surface-400 mb-4">
                Are you sure you want to delete <span className="text-white font-medium">{showDeleteConfirm.name}</span>? 
                This will delete all family data including members, tasks, documents, and more. This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 rounded-lg bg-surface-700 hover:bg-surface-600 text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteFamilyMutation.mutate(showDeleteConfirm.id)}
                  disabled={deleteFamilyMutation.isPending}
                  className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-50"
                >
                  {deleteFamilyMutation.isPending ? 'Deleting...' : 'Delete Family'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

